// src/api/tariffAPI.js
import apiClient from './client';

const TARIFFS_URL = '/api/v1/tariffs/';
const COMPANY_TARIFFS_URL = '/api/v1/companies/';

export const getTariffs = async (params) => {
  try {
    // Always include enabled parameter - if not specified, get both enabled and disabled
    const requestParams = { ...params };
    if (!requestParams.hasOwnProperty('enabled')) {
      // If enabled is not specified, we need to get all tariffs (both enabled and disabled)
      // We'll make two separate requests and combine them
      try {
        const [enabledResponse, disabledResponse] = await Promise.all([
          apiClient.get(TARIFFS_URL, { params: { ...requestParams, enabled: true } }),
          apiClient.get(TARIFFS_URL, { params: { ...requestParams, enabled: false } })
        ]);
        
        // Combine the results and remove duplicates
        const allTariffs = [...enabledResponse.data, ...disabledResponse.data];
        const uniqueTariffs = allTariffs.filter((tariff, index, self) => 
          index === self.findIndex(t => t.TariffsId === tariff.TariffsId)
        );
        return uniqueTariffs;
      } catch (fallbackError) {
        // If the combined approach fails, try with enabled=true as fallback
        requestParams.enabled = true;
      }
    }
    
    const response = await apiClient.get(TARIFFS_URL, { params: requestParams });
    return response.data;
  } catch (error) {
    console.error('Error fetching tariffs:', error);
    throw error;
  }
};

export const getCompanyTariffs = async (companyId, enabled, tariffType) => {
  try {
    const params = {};
    if (tariffType) params.tariff_type = tariffType;
    
    // Always include enabled parameter
    if (enabled !== undefined) {
      params.enabled = enabled;
    } else {
      // If enabled is not specified, we need to get all tariffs (both enabled and disabled)
      try {
        const [enabledResponse, disabledResponse] = await Promise.all([
          apiClient.get(`${COMPANY_TARIFFS_URL}${companyId}/tariffs`, { 
            params: { ...params, enabled: true } 
          }),
          apiClient.get(`${COMPANY_TARIFFS_URL}${companyId}/tariffs`, { 
            params: { ...params, enabled: false } 
          })
        ]);
        
        // Combine the results and remove duplicates
        const allTariffs = [...enabledResponse.data, ...disabledResponse.data];
        const uniqueTariffs = allTariffs.filter((tariff, index, self) => 
          index === self.findIndex(t => t.TariffsId === tariff.TariffsId)
        );
        return uniqueTariffs;
      } catch (fallbackError) {
        // If the combined approach fails, try with enabled=true as fallback
        params.enabled = true;
      }
    }
    
    const response = await apiClient.get(`${COMPANY_TARIFFS_URL}${companyId}/tariffs`, { params });
    return response.data;
  } catch (error) {
    console.error(`Error fetching tariffs for company ${companyId}:`, error);
    throw error;
  }
};

export const getTariff = async (tariffId) => {
  try {
    // For single tariff fetch, we try both enabled states since we need the specific tariff
    // regardless of its enabled status
    
    // First try with enabled=true
    try {
      const response = await apiClient.get(`${TARIFFS_URL}${tariffId}`, {
        params: { enabled: true }
      });
      return response.data;
    } catch (enabledError) {
      // If that fails, try with enabled=false
      try {
        const response = await apiClient.get(`${TARIFFS_URL}${tariffId}`, {
          params: { enabled: false }
        });
        return response.data;
      } catch (disabledError) {
        // If both fail, throw the original error
        throw enabledError;
      }
    }
  } catch (error) {
    console.error(`Error fetching tariff ${tariffId}:`, error);
    throw error;
  }
};

export const createTariff = async (tariffData) => {
  try {
    const response = await apiClient.post(TARIFFS_URL, tariffData);
    return response.data;
  } catch (error) {
    console.error('Error creating tariff:', error);
    throw error;
  }
};

export const updateTariff = async (tariffId, tariffData) => {
  try {
    const response = await apiClient.put(`${TARIFFS_URL}${tariffId}`, tariffData);
    return response.data;
  } catch (error) {
    console.error(`Error updating tariff ${tariffId}:`, error);
    throw error;
  }
};

export const deleteTariff = async (tariffId) => {
  try {
    await apiClient.delete(`${TARIFFS_URL}${tariffId}`);
  } catch (error) {
    console.error(`Error deleting tariff ${tariffId}:`, error);
    throw error;
  }
};